import { useEffect } from "react";
import { PhoneIcon } from "@heroicons/react/24/outline";

import Button from "components/Button";
import { OUTLINE_BUTTON_CLASS } from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import LeadLanding from "components/LeadLanding";
import ScreenChrome from "components/ScreenChrome";

import { useLeadFlowAnalytics } from "../analytics";
import {
  callLineFor,
  CALL_PROMPT_TEXT,
  CLOSED_COPY,
  confirmPromptCopy,
  EXPIRED_COPY,
  landingCards,
  landingCopy,
  RETRY_CONFIRM_COPY,
} from "../copy";
import {
  openableQuotePathOf,
  type GrantMethod,
  type LeadResult,
} from "../submit";
import {
  useSubmissionStatus,
  type SubmissionStatusPhase,
} from "../useSubmissionStatus";

function ConfirmPrompt({
  verification,
  disabled,
  onConfirm,
}: {
  verification: NonNullable<LeadResult["verification"]>;
  disabled: boolean;
  onConfirm: (method: GrantMethod) => void;
}) {
  const copy = confirmPromptCopy(verification);
  if (copy === null) return null;
  return (
    <div className="mt-8 rounded-2xl border border-oat-200 bg-white p-5 sm:p-6">
      <h2 className="font-serif text-[22px] leading-tight text-soil">
        {copy.title}
      </h2>
      <p className="mt-1.5 text-base leading-normal text-oat-700">
        Your details and updates live in your account. {copy.delivery}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {copy.actions.map((action, index) => (
          <button
            key={action.method}
            type="button"
            className={
              index === 0 ? CONTINUE_BUTTON_CLASS : OUTLINE_BUTTON_CLASS
            }
            disabled={disabled}
            onClick={() => onConfirm(action.method)}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RetryConfirm({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mt-8 rounded-2xl border border-oat-200 bg-white p-5 sm:p-6">
      <p className="text-base leading-normal text-oat-700">
        {RETRY_CONFIRM_COPY.body}
      </p>
      <div className="mt-4">
        <button
          type="button"
          className={CONTINUE_BUTTON_CLASS}
          onClick={onRetry}
        >
          {RETRY_CONFIRM_COPY.button}
        </button>
      </div>
    </div>
  );
}

/** The confirmation area under the landing: the prompt while the
 * submission awaits a code or link, and nothing once it is settled. */
function ConfirmationArea({
  result,
  phase,
  onConfirm,
  onRetry,
}: {
  result: LeadResult;
  phase: SubmissionStatusPhase;
  onConfirm: (method: GrantMethod) => void;
  onRetry: () => void;
}) {
  if (result.status !== "awaiting_confirmation") return null;
  if (phase === "retry") return <RetryConfirm onRetry={onRetry} />;
  if (result.verification === null) return null;
  return (
    <ConfirmPrompt
      verification={result.verification}
      disabled={phase === "checking"}
      onConfirm={onConfirm}
    />
  );
}

function CallLineButton({ kind }: { kind: LeadResult["leadKind"] }) {
  const line = callLineFor(kind);
  return (
    <Button
      href={line.href}
      variant="compact"
      className="w-full sm:w-auto"
      icon={
        <PhoneIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
      }
    >
      Call <span className="ph-no-capture">{line.label}</span>
    </Button>
  );
}

function ClosedScreen({ kind }: { kind: LeadResult["leadKind"] }) {
  return (
    <ScreenChrome
      kicker={CLOSED_COPY.kicker}
      titleA={CLOSED_COPY.titleA}
      titleB={CLOSED_COPY.titleB}
      blurb={CLOSED_COPY.body}
    >
      <div className="mt-8">
        <CallLineButton kind={kind} />
      </div>
    </ScreenChrome>
  );
}

function ExpiredScreen({ onStartAgain }: { onStartAgain: () => void }) {
  return (
    <ScreenChrome
      kicker={EXPIRED_COPY.kicker}
      titleA={EXPIRED_COPY.titleA}
      titleB={EXPIRED_COPY.titleB}
      blurb={EXPIRED_COPY.body}
    >
      <div className="mt-8">
        <button
          type="button"
          className={CONTINUE_BUTTON_CLASS}
          onClick={onStartAgain}
        >
          {EXPIRED_COPY.button}
        </button>
      </div>
    </ScreenChrome>
  );
}

/** The landing after a submission that did not go straight to a quote:
 * what was received, what happens next, the phone line, and the way to
 * confirm the account while the submission awaits it. Everything it reads
 * is on the stored result, and the server is asked where the submission
 * stands each time the screen opens. */
export default function DoneStep({
  result,
  onResult,
  onConfirm,
  onStartAgain,
}: {
  result: LeadResult;
  onResult: (result: LeadResult) => void;
  onConfirm: (method: GrantMethod) => void;
  onStartAgain: () => void;
}) {
  const { confirmPromptShown } = useLeadFlowAnalytics();
  const { phase, retry } = useSubmissionStatus(result, onResult);
  const answers = result.landingAnswers;

  const promptShown =
    result.status === "awaiting_confirmation" && result.verification !== null;
  useEffect(() => {
    if (promptShown) confirmPromptShown();
    // The prompt is reported once per time it appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptShown]);

  if (result.status === "expired") {
    return <ExpiredScreen onStartAgain={onStartAgain} />;
  }

  if (result.status === "closed")
    return <ClosedScreen kind={result.leadKind} />;

  const copy = landingCopy(result, answers);
  const quotePath = openableQuotePathOf(result);

  return (
    <ScreenChrome
      kicker={copy.kicker}
      titleA={copy.titleA}
      titleB={copy.titleB}
      blurb={copy.body}
    >
      <LeadLanding cards={landingCards(result, answers)} steps={copy.steps} />
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <CallLineButton kind={result.leadKind} />
        <span className="text-base leading-normal text-oat-700">
          {CALL_PROMPT_TEXT}
        </span>
        {quotePath && (
          <Button
            href={quotePath}
            variant="compact"
            className="w-full sm:w-auto"
          >
            View your quote
          </Button>
        )}
      </div>
      <ConfirmationArea
        result={result}
        phase={phase}
        onConfirm={onConfirm}
        onRetry={retry}
      />
    </ScreenChrome>
  );
}
